import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Row,
  Col,
  Typography,
  Tabs,
  Space,
  Tag,
  Descriptions,
  message,
  Modal,
  Alert,
  Divider,
  Spin
} from 'antd';
import {
  UserOutlined,
  LockOutlined,
  SafetyOutlined,
  EditOutlined,
  SaveOutlined,
  ExclamationCircleOutlined,
  BankOutlined
} from '@ant-design/icons';
import axios from 'axios';
import MerchantBankAccounts from './MerchantBankAccounts';

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;
const { TextArea } = Input;

const MerchantSettings = () => {
  const [profileForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [limitsForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [limitsLoading, setLimitsLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [editingLimits, setEditingLimits] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('merchant_access_token');
      if (!token) {
        message.error('Token tidak ditemukan. Silakan login kembali.');
        return;
      }
      const response = await axios.get('/api/merchant/settings', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setSettings(response.data);

      // Populate profile form
      if (response.data.profile) {
        profileForm.setFieldsValue(response.data.profile);
      }

      // Populate limits form
      if (response.data.limits) {
        limitsForm.setFieldsValue({
          daily_limit: parseFloat(response.data.limits.daily_limit),
          monthly_limit: parseFloat(response.data.limits.monthly_limit)
        });
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      message.error('Gagal memuat pengaturan');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async (values) => {
    try {
      setProfileLoading(true);
      const token = localStorage.getItem('merchant_access_token');
      if (!token) {
        message.error('Token tidak ditemukan. Silakan login kembali.');
        return;
      }
      await axios.put('/api/merchant/settings/profile', values, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      message.success('Profil berhasil diperbarui');
      fetchSettings(); // Refresh data
    } catch (error) {
      console.error('Update profile error:', error);
      const errorMessage = error.response?.data?.error || 'Gagal memperbarui profil';
      message.error(errorMessage);
    } finally {
      setProfileLoading(false);
    }
  };

  const handleUpdatePassword = async (values) => {
    try {
      setPasswordLoading(true);

      // Remove confirm password from request
      const { confirm_password, ...passwordData } = values;

      const token = localStorage.getItem('merchant_access_token');
      if (!token) {
        message.error('Token tidak ditemukan. Silakan login kembali.');
        return;
      }

      await axios.put('/api/merchant/settings/password', passwordData, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      message.success('Password berhasil diperbarui');
      passwordForm.resetFields();
    } catch (error) {
      console.error('Update password error:', error);
      const errorMessage = error.response?.data?.error || 'Gagal memperbarui password';
      message.error(errorMessage);
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleUpdateLimits = async (values) => {
    try {
      setLimitsLoading(true);
      const token = localStorage.getItem('merchant_access_token');
      if (!token) {
        message.error('Token tidak ditemukan. Silakan login kembali.');
        return;
      }
      await axios.put('/api/merchant/settings/limits', values, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      message.success('Limit transaksi berhasil diperbarui');
      setEditingLimits(false);
      fetchSettings(); // Refresh data
    } catch (error) {
      console.error('Update limits error:', error);
      const errorMessage = error.response?.data?.error || 'Gagal memperbarui limit transaksi';
      message.error(errorMessage);
    } finally {
      setLimitsLoading(false);
    }
  };


  const formatCurrency = (amount) => {
    const numericAmount = Number(amount);
    if (isNaN(numericAmount)) return 'Rp 0';
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(numericAmount);
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'green',
      pending: 'orange',
      suspended: 'red',
      blocked: 'red'
    };
    return colors[status] || 'default';
  };

  const getKycStatusColor = (status) => {
    const colors = {
      verified: 'green',
      pending: 'orange',
      unverified: 'red',
      rejected: 'red'
    };
    return colors[status] || 'default';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spin size="large" />
      </div>
    );
  }

  const profile = settings?.profile || {};
  const limits = settings?.limits || {};

  return (
    <div className="p-6">
      <div className="mb-6">
        <Title level={2}>Pengaturan Akun</Title>
        <Text type="secondary">Kelola profil dan keamanan akun merchant Anda</Text>
      </div>

      <Tabs defaultActiveKey="profile" type="card">
        {/* Profile Tab */}
        <TabPane
          tab={
            <span>
              <UserOutlined />
              Profil
            </span>
          }
          key="profile"
        >
          <Row gutter={24}>
            <Col xl={16} lg={24} md={24} sm={24} xs={24}>
              <Card title="Informasi Profil" extra={<EditOutlined />}>
                <Form
                  form={profileForm}
                  layout="vertical"
                  onFinish={handleUpdateProfile}
                >
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label="Nama Lengkap"
                        name="full_name"
                        rules={[{ required: true, message: 'Nama lengkap diperlukan!' }]}
                      >
                        <Input size="large" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Nomor Telepon"
                        name="phone"
                        rules={[{ required: true, message: 'Nomor telepon diperlukan!' }]}
                      >
                        <Input size="large" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label="Nama Perusahaan"
                        name="company_name"
                      >
                        <Input size="large" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        label="Jenis Bisnis"
                        name="business_type"
                      >
                        <Select size="large" placeholder="Pilih jenis bisnis">
                          <Option value="retail">Retail</Option>
                          <Option value="restaurant">Restaurant</Option>
                          <Option value="services">Services</Option>
                          <Option value="ecommerce">E-commerce</Option>
                          <Option value="education">Education</Option>
                          <Option value="healthcare">Healthcare</Option>
                          <Option value="others">Others</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item
                    label="Alamat"
                    name="address"
                  >
                    <TextArea rows={3} />
                  </Form.Item>

                  <Row gutter={16}>
                    <Col span={8}>
                      <Form.Item
                        label="Kota"
                        name="city"
                      >
                        <Input size="large" />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        label="Provinsi"
                        name="state"
                      >
                        <Input size="large" />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        label="Kode Pos"
                        name="postal_code"
                      >
                        <Input size="large" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={profileLoading}
                      size="large"
                      icon={<SaveOutlined />}
                    >
                      {profileLoading ? 'Menyimpan...' : 'Simpan Perubahan'}
                    </Button>
                  </Form.Item>
                </Form>
              </Card>
            </Col>

            <Col xl={8} lg={24} md={24} sm={24} xs={24}>
              <Card title="Status Akun" className="mb-6">
                <Descriptions column={1}>
                  <Descriptions.Item label="Member ID">
                    <Text copyable style={{ fontFamily: 'monospace' }}>
                      {profile.member_id}
                    </Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Email">
                    <Text>{profile.email}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Status Akun">
                    <Tag color={getStatusColor(profile.status)}>
                      {profile.status?.toUpperCase()}
                    </Tag>
                  </Descriptions.Item>

                  <Descriptions.Item label="Tanggal Daftar">
                    {new Date(profile.created_at).toLocaleDateString('id-ID')}
                  </Descriptions.Item>
                </Descriptions>
              </Card>

              {/* Limits Info - Hidden for now */}
              {false && limits && (
                <Card
                  title="Limit Transaksi"
                  extra={
                    <Button
                      type={editingLimits ? "default" : "primary"}
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => setEditingLimits(!editingLimits)}
                    >
                      {editingLimits ? 'Batal' : 'Edit'}
                    </Button>
                  }
                >
                  {!editingLimits ? (
                    <Descriptions column={1}>
                      <Descriptions.Item label="Limit Harian">
                        <div>
                          <Text strong>{formatCurrency(limits.daily_limit)}</Text>
                          <br />
                          <Text type="secondary">
                            Terpakai: {formatCurrency(limits.daily_spent)}
                          </Text>
                        </div>
                      </Descriptions.Item>
                      <Descriptions.Item label="Limit Bulanan">
                        <div>
                          <Text strong>{formatCurrency(limits.monthly_limit)}</Text>
                          <br />
                          <Text type="secondary">
                            Terpakai: {formatCurrency(limits.monthly_spent)}
                          </Text>
                        </div>
                      </Descriptions.Item>
                    </Descriptions>
                  ) : (
                    <Form
                      form={limitsForm}
                      layout="vertical"
                      onFinish={handleUpdateLimits}
                    >
                      <Form.Item
                        label="Limit Harian (Rp)"
                        name="daily_limit"
                        rules={[
                          { required: true, message: 'Limit harian diperlukan!' },
                          {
                            validator: (_, value) => {
                              if (value < 0) {
                                return Promise.reject(new Error('Limit harian tidak boleh negatif'));
                              }
                              return Promise.resolve();
                            }
                          }
                        ]}
                      >
                        <Input
                          type="number"
                          size="large"
                          placeholder="0"
                          min={0}
                        />
                      </Form.Item>

                      <Form.Item
                        label="Limit Bulanan (Rp)"
                        name="monthly_limit"
                        rules={[
                          { required: true, message: 'Limit bulanan diperlukan!' },
                          {
                            validator: (_, value) => {
                              if (value < 0) {
                                return Promise.reject(new Error('Limit bulanan tidak boleh negatif'));
                              }
                              return Promise.resolve();
                            }
                          }
                        ]}
                      >
                        <Input
                          type="number"
                          size="large"
                          placeholder="0"
                          min={0}
                        />
                      </Form.Item>

                      <Form.Item>
                        <Space>
                          <Button
                            type="primary"
                            htmlType="submit"
                            loading={limitsLoading}
                            icon={<SaveOutlined />}
                          >
                            {limitsLoading ? 'Menyimpan...' : 'Simpan'}
                          </Button>
                          <Button onClick={() => setEditingLimits(false)}>
                            Batal
                          </Button>
                        </Space>
                      </Form.Item>
                    </Form>
                  )}
                </Card>
              )}
            </Col>
          </Row>
        </TabPane>

        {/* Security Tab */}
        <TabPane
          tab={
            <span>
              <LockOutlined />
              Keamanan
            </span>
          }
          key="security"
        >
          <Row gutter={24}>
            <Col xl={12} lg={24} md={24} sm={24} xs={24}>
              <Card title="Ubah Password" extra={<SafetyOutlined />}>
                <Alert
                  message="Keamanan Password"
                  description="Gunakan password yang kuat dengan kombinasi huruf besar, huruf kecil, angka, dan simbol."
                  type="info"
                  showIcon
                  style={{ marginBottom: 24 }}
                />

                <Form
                  form={passwordForm}
                  layout="vertical"
                  onFinish={handleUpdatePassword}
                >
                  <Form.Item
                    label="Password Lama"
                    name="current_password"
                    rules={[{ required: true, message: 'Password lama diperlukan!' }]}
                  >
                    <Input.Password size="large" />
                  </Form.Item>

                  <Form.Item
                    label="Password Baru"
                    name="new_password"
                    rules={[
                      { required: true, message: 'Password baru diperlukan!' },
                      { min: 6, message: 'Password minimal 6 karakter!' }
                    ]}
                  >
                    <Input.Password size="large" />
                  </Form.Item>

                  <Form.Item
                    label="Konfirmasi Password Baru"
                    name="confirm_password"
                    dependencies={['new_password']}
                    rules={[
                      { required: true, message: 'Konfirmasi password diperlukan!' },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue('new_password') === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error('Password tidak cocok!'));
                        },
                      }),
                    ]}
                  >
                    <Input.Password size="large" />
                  </Form.Item>

                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={passwordLoading}
                      size="large"
                      icon={<LockOutlined />}
                    >
                      {passwordLoading ? 'Memperbarui...' : 'Perbarui Password'}
                    </Button>
                  </Form.Item>
                </Form>
              </Card>
            </Col>

            {/* Keamanan Akun card hidden */}
          </Row>
        </TabPane>

        {/* Rekening Bank Tab */}
        <TabPane
          tab={
            <span>
              <BankOutlined />
              Rekening Bank
            </span>
          }
          key="bank-accounts"
        >
          <MerchantBankAccounts />
        </TabPane>
      </Tabs>

    </div>
  );
};

export default MerchantSettings;